import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { X, RefreshCw, Loader2, Smartphone, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { formatDateTime } from '../lib/dateFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const providerLabel = (p) => {
  switch (p) {
    case 'apns': return 'Apple (iOS)';
    case 'fcm': return 'Google (Android/Chrome)';
    case 'mozilla': return 'Mozilla (Firefox)';
    case 'wns': return 'Microsoft (Edge)';
    default: return p || 'unknown';
  }
};

const statusBadge = (row) => {
  const code = row.status_code;
  if (row.success) return { label: `${code} OK`, cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' };
  if (code === 410 || code === 404) return { label: `${code} Gone`, cls: 'bg-amber-100 text-amber-700 border-amber-200' };
  if (code === 429) return { label: '429 Rate-limit', cls: 'bg-amber-100 text-amber-700 border-amber-200' };
  if (code === 401 || code === 403) return { label: `${code} Auth`, cls: 'bg-red-100 text-red-700 border-red-200' };
  return { label: `${code ?? '—'} Fail`, cls: 'bg-red-100 text-red-700 border-red-200' };
};

const fmt = (iso) => formatDateTime(iso);

export const DriverPushAuditModal = ({ driverId, driverName, authHeaders, onClose }) => {
  const [data, setData] = useState({ rows: [], summary: null });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/admin/drivers/${driverId}/push-audit`, { headers: authHeaders });
      setData(d);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [driverId]);

  const successRate = data?.summary?.success_rate_pct;
  const hint = useMemo(() => {
    if (!data?.rows?.length) return null;
    const rows = data.rows;
    const failing = rows.filter((r) => !r.success);
    if (failing.length === 0) return null;
    const goneCount = failing.filter((r) => r.status_code === 410 || r.status_code === 404).length;
    if (goneCount === failing.length) {
      return 'All failures are 410 Gone — the driver has unsubscribed or wiped notifications. Ask them to re-enable in the app.';
    }
    const apnsFail = failing.filter((r) => r.provider === 'apns').length;
    if (apnsFail > 0) {
      return `${apnsFail} APNs failure${apnsFail === 1 ? '' : 's'} — usually means Focus mode is on, Low Power Mode is enabled, or Notifications are restricted in iOS Settings.`;
    }
    return `${failing.length} of ${rows.length} attempts failed. See error column for detail.`;
  }, [data]);

  return (
    <div
      className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="push-audit-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 bg-[#0F172A] text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Smartphone className="w-5 h-5 text-emerald-300" />
            <div>
              <h2 className="font-bold text-base leading-tight">Push delivery audit · {driverName}</h2>
              <p className="text-[11px] text-white/60">Last 50 attempts (newest first)</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={load}
              className="p-1.5 rounded-md hover:bg-white/10"
              title="Refresh"
              data-testid="audit-refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button type="button" onClick={onClose} className="p-1.5 rounded-md hover:bg-white/10">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="px-5 py-3 border-b border-neutral-200 bg-neutral-50 flex items-center gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold">Last 24h</div>
            <div className="text-2xl font-bold text-[#0F172A]">
              {data?.summary?.success_24h ?? 0}/{data?.summary?.total_24h ?? 0}
              <span className="text-sm font-medium text-neutral-500 ml-1">
                {successRate !== null && successRate !== undefined ? `(${successRate}%)` : ''}
              </span>
            </div>
            <div className="text-[10px] text-neutral-500">delivered to provider</div>
          </div>
          {hint && (
            <div className="ml-auto max-w-md flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200">
              <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800 leading-snug">{hint}</p>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading && data.rows.length === 0 ? (
            <div className="p-8 flex items-center justify-center gap-2 text-neutral-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading audit log…
            </div>
          ) : data.rows.length === 0 ? (
            <div className="p-8 text-center text-sm text-neutral-500">
              <CheckCircle2 className="w-8 h-8 mx-auto text-neutral-300 mb-2" />
              No push attempts recorded yet.<br />
              They'll appear here as soon as a broadcast or order assignment fires.
            </div>
          ) : (
            <table className="w-full text-xs" data-testid="audit-table">
              <thead className="bg-neutral-50 text-left text-[10px] uppercase tracking-wider text-neutral-500 sticky top-0">
                <tr>
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">Provider</th>
                  <th className="px-3 py-2">Device</th>
                  <th className="px-3 py-2">Title / tag</th>
                  <th className="px-3 py-2">Urgency</th>
                  <th className="px-3 py-2 text-right">Result</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {data.rows.map((r) => {
                  const b = statusBadge(r);
                  return (
                    <tr key={r.id} data-testid={`audit-row-${r.id}`}>
                      <td className="px-3 py-2 whitespace-nowrap text-neutral-600">{fmt(r.created_at)}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${
                          r.provider === 'apns' ? 'bg-[#0F172A] text-white' :
                          r.provider === 'fcm' ? 'bg-emerald-700 text-white' :
                          'bg-neutral-200 text-neutral-700'
                        }`}>
                          {providerLabel(r.provider)}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono text-[10px] text-neutral-500">…{r.endpoint_suffix}</td>
                      <td className="px-3 py-2">
                        <div className="font-semibold text-[#0F172A] truncate max-w-[180px]" title={r.title}>{r.title || '—'}</div>
                        {r.tag && <div className="text-[10px] text-neutral-400 truncate max-w-[180px]" title={r.tag}>{r.tag}</div>}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`text-[10px] font-bold uppercase ${r.urgency === 'high' ? 'text-red-600' : 'text-neutral-500'}`}>
                          {r.urgency}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold border ${b.cls}`}>
                          {b.label}
                        </span>
                        {r.error && (
                          <div className="text-[10px] text-red-600 mt-1 max-w-[220px] truncate text-right" title={r.error}>
                            {r.error}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-5 py-3 border-t border-neutral-200 bg-neutral-50 text-[10px] text-neutral-500">
          <b>How to read:</b> <span className="font-mono">201/200</span> = sent to provider successfully (still may not show on device if Focus/Low-Power blocks).
          <span className="font-mono"> 410/404</span> = subscription is dead (driver removed app or revoked permissions).
          <span className="font-mono"> 429</span> = provider rate-limited.
        </div>
      </div>
    </div>
  );
};

export default DriverPushAuditModal;
